import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { fleetService } from '../../services/fleetService';
import { Fleet } from '../../types';

interface PublicReservationLinksProps {
  organizationId: string;
}

const PublicReservationLinks: React.FC<PublicReservationLinksProps> = ({ organizationId }) => {
  const { t } = useTranslation();
  const [fleets, setFleets] = useState<Fleet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    fetchFleets();
  }, [organizationId]);

  const fetchFleets = async () => {
    try {
      setLoading(true);
      setError(null);
      const fleetsData = await fleetService.getFleets(organizationId);
      setFleets(fleetsData);
    } catch (err) {
      console.error('Error fetching fleets:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublicLink = async (fleet: Fleet) => {
    try {
      setActionLoading(fleet.id);
      const newStatus = !fleet.publicLinkEnabled;
      
      await fleetService.updateFleetReservationSettings(
        fleet.id,
        { publicLinkEnabled: newStatus },
        'current-user' // Normalement, cela devrait être l'ID de l'utilisateur actuel
      );
      
      // Mettre à jour l'état local
      setFleets(fleets.map(f => 
        f.id === fleet.id 
          ? { ...f, publicLinkEnabled: newStatus } 
          : f
      ));
      
    } catch (err) {
      console.error('Error toggling public link:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateLink = async (fleet: Fleet) => {
    try {
      setActionLoading(fleet.id);
      
      const publicLinkId = await fleetService.generatePublicLink(fleet.id);
      
      // Mettre à jour l'état local
      setFleets(fleets.map(f => 
        f.id === fleet.id 
          ? { ...f, publicLinkId } 
          : f
      ));
      
    } catch (err) {
      console.error('Error generating link:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCopyLink = (publicLinkId: string) => {
    const url = `${window.location.origin}/reserve/${publicLinkId}`;
    navigator.clipboard.writeText(url)
      .then(() => {
        setSnackbarMessage(t('reservation:publicLinks.linkCopied'));
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Error copying to clipboard:', err);
      });
  };

  const getStatusChip = (enabled: boolean) => {
    return enabled 
      ? <Chip label={t('reservation:publicLinks.enabled')} color="success" size="small" />
      : <Chip label={t('reservation:publicLinks.disabled')} color="default" size="small" />;
  };

  const getPublicLinkUrl = (fleet: Fleet) => {
    if (!fleet.publicLinkEnabled) {
      return '-';
    }
    
    if (!fleet.publicLinkId) {
      return t('reservation:publicLinks.notGenerated');
    }
    
    const url = `${window.location.origin}/reserve/${fleet.publicLinkId}`;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ mr: 1, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {url}
        </Typography>
        <Tooltip title={t('reservation:publicLinks.copyLink')}>
          <IconButton 
            size="small" 
            onClick={() => handleCopyLink(fleet.publicLinkId as string)}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          {t('reservation:publicLinks.title')}
        </Typography>
        <Button 
          variant="outlined" 
          onClick={fetchFleets}
          startIcon={<RefreshIcon />}
        >
          {t('reservation:common.refresh')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : fleets.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {t('reservation:common.noData')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('reservation:common.fleet')}</TableCell>
                <TableCell>{t('reservation:publicLinks.status')}</TableCell>
                <TableCell>{t('reservation:publicLinks.url')}</TableCell>
                <TableCell>{t('reservation:common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fleets.map((fleet) => (
                <TableRow key={fleet.id}>
                  <TableCell>{fleet.name}</TableCell>
                  <TableCell>{getStatusChip(fleet.publicLinkEnabled || false)}</TableCell>
                  <TableCell>{getPublicLinkUrl(fleet)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex' }}>
                      {actionLoading === fleet.id ? (
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                      ) : (
                        <>
                          <Tooltip title={fleet.publicLinkEnabled 
                            ? t('reservation:publicLinks.actions.disable') 
                            : t('reservation:publicLinks.actions.enable')
                          }>
                            <IconButton 
                              size="small" 
                              color={fleet.publicLinkEnabled ? "error" : "success"}
                              onClick={() => handleTogglePublicLink(fleet)}
                            >
                              {fleet.publicLinkEnabled 
                                ? <CancelIcon fontSize="small" /> 
                                : <CheckCircleIcon fontSize="small" />
                              }
                            </IconButton>
                          </Tooltip>
                          
                          {fleet.publicLinkEnabled && (
                            <Tooltip title={fleet.publicLinkId 
                              ? t('reservation:publicLinks.actions.regenerate') 
                              : t('reservation:publicLinks.actions.generate')
                            }>
                              <IconButton 
                                size="small" 
                                onClick={() => handleGenerateLink(fleet)}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default PublicReservationLinks;
